import { Alert, Badge, Group, Stack } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useRunStream } from '../../hooks/use-run-stream';
import { useAuthStore } from '../../state/auth-store';
import { useRunStreamStore } from '../../state/run-stream-store';
import { generationModelLabel } from '../../utils/generation-models';
import { ClearButton } from './ClearButton';
import { CopyButton } from './CopyButton';
import { CostLabel } from './CostLabel';
import { OutputMarkdown } from './OutputMarkdown';
import { PremisePacketCards } from './PremisePacketCards';
import { SectionHeading } from './SectionHeading';
import { ViewPromptButton } from './ViewPromptButton';

/**
 * Renders the output surface for the active or selected run.
 */
export function OutputPane() {
  const runId = useRunStreamStore((s) => s.runId);
  const { deltaText, status, error, runResult } = useRunStream(runId);
  const user = useAuthStore((s) => s.user);

  const [copied, setCopied] = useState(false);
  const copiedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleCopied = useCallback(() => {
    setCopied(true);
    if (copiedTimerRef.current !== null) {
      clearTimeout(copiedTimerRef.current);
    }
    copiedTimerRef.current = setTimeout(() => {
      setCopied(false);
    }, 2000);
  }, []);

  useEffect(() => {
    return () => {
      if (copiedTimerRef.current !== null) {
        clearTimeout(copiedTimerRef.current);
      }
    };
  }, []);

  const prevStatusRef = useRef(status);
  useEffect(() => {
    if (status === 'error' && prevStatusRef.current !== 'error' && error) {
      notifications.show({
        color: 'red',
        title: 'Generation error',
        message: error,
        autoClose: 8000,
      });
    }
    prevStatusRef.current = status;
  }, [status, error]);

  const role = user?.role ?? 'writer';
  const cost = runResult?.estimatedCostUsd ?? null;
  const copyText = deltaText || runResult?.outputMarkdown || '';
  const showPremiseCards =
    runResult?.mode === 'scratch_premise_set' &&
    (runResult.structuredOutput?.premisePackets.length ?? 0) > 0;

  return (
    <Stack gap="sm" h="100%" data-testid="output-pane">
      <Group justify="space-between" align="center" wrap="nowrap">
        <SectionHeading>Output</SectionHeading>

        <Group gap="xs" wrap="nowrap">
          {role === 'system_owner' && runResult?.id && <ViewPromptButton runId={runResult.id} />}
          <CopyButton text={copyText} onCopied={handleCopied} disabled={!copyText} />
          <ClearButton disabled={status === 'streaming'} />
        </Group>
      </Group>

      {status === 'error' && error && (
        <Alert
          variant="light"
          color="red"
          title="Generation failed"
          data-testid="output-failure-alert"
        >
          {error}
        </Alert>
      )}

      <Stack
        flex={1}
        style={{
          minHeight: 200,
          border: '1px solid var(--mantine-color-default-border)',
          borderRadius: 'var(--mantine-radius-sm)',
          overflow: 'hidden',
          background: 'var(--mantine-color-body)',
        }}
        data-testid="output-body"
      >
        {showPremiseCards ? (
          <PremisePacketCards run={runResult} />
        ) : (
          <OutputMarkdown text={deltaText} status={status} />
        )}
      </Stack>

      <Group gap="xs" wrap="wrap" data-testid="output-status">
        {runResult && (
          <Badge color="accent" variant="light" size="sm">
            {runResult.generationModel?.label ?? generationModelLabel(null)}
          </Badge>
        )}
        <CostLabel cost={cost} status={status} role={role} copied={copied} />
      </Group>
    </Stack>
  );
}
